// src/prototype/components/InitiativesUploadBand.tsx
// ============================================================
// INITIATIVES UPLOAD BAND
// Standalone expandable band for existing initiatives upload
// ============================================================

import { useEffect, useRef, useState } from 'react';
// @ts-ignore - AuthContext is still authored in JSX in this repo.
import { useAuth } from '@/contexts/AuthContext';
// @ts-ignore - service is still authored in JS in this repo.
import {
  batchPromoteCandidates,
  createUseCasesFromDocument,
  findFromDocumentOutcome,
  isSuccessfulFromDocumentOutcome,
} from '@/services/useCaseIntakeService';
import { io, type Socket } from 'socket.io-client';
import {
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Upload,
  FileText,
  FileSpreadsheet,
  Info,
  CheckCircle2,
} from 'lucide-react';

const API_BASE_URL = (import.meta as ImportMeta & {
  env?: Record<string, string | undefined>;
}).env?.VITE_API_URL || 'http://localhost:8080';
const SOCKET_BASE_URL = (import.meta as ImportMeta & {
  env?: Record<string, string | undefined>;
}).env?.VITE_USE_CASE_SOCKET_URL;
// Socket.IO path; differs from the default when the worker is reached through the load balancer
const SOCKET_PATH = (import.meta as ImportMeta & {
  env?: Record<string, string | undefined>;
}).env?.VITE_USE_CASE_SOCKET_PATH || '/socket.io';
const INITIATIVES_DOCUMENT_KIND = 'initiative_inventory';
const ALLOWED_EXTENSIONS = ['txt', 'csv', 'pdf', 'md'];
const ALLOWED_EXTENSIONS_LABEL = 'TXT, CSV, PDF, MD';
const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;
const LISTING_POLL_INTERVAL_MS = 5000;
const USE_CASES_FETCH_PAGE_SIZE = 100;
const USE_CASES_TABLE_PAGE_SIZE = 10;

function serializeUploadedDocuments(documents: UploadedDocument[]) {
  return documents
    .map((document) =>
      [document.id, document.name, document.status, document.size, document.uploadedAt, document.fileType].join('\0'),
    )
    .join('\n');
}

function serializeDocumentListings(listings: DocumentListing[]) {
  return listings
    .map((listing) => [listing.id, listing.fileName, String(listing.use_case_count)].join('\0'))
    .join('\n');
}

function serializeUseCases(useCases: UseCaseDecisionRecord[]) {
  return useCases
    .map((useCase) =>
      [
        String(useCase.use_case_id ?? ''),
        useCase.name ?? '',
        useCase.category ?? '',
        useCase.description ?? '',
        useCase.decision_status ?? '',
      ].join('\0'),
    )
    .join('\n');
}

// Sample data based on New Use Case Candidates CSV structure
const sampleInitiatives = [
  {
    name: 'Patient Readmission Risk Model',
    category: 'Clinical Analytics',
    source: 'Internal Strategy Doc',
    notes: 'Q2 priority from CMO',
    status: 'New',
  },
  {
    name: 'OR Block Scheduling Optimization',
    category: 'Operational Efficiency',
    source: 'Consulting Engagement',
    notes: 'Stalled due to resource constraints',
    status: 'New',
  },
  {
    name: 'Revenue Cycle Automation',
    category: 'Revenue Integrity',
    source: 'CFO Initiative List',
    notes: 'Vendor demos completed',
    status: 'New',
  },
  {
    name: 'Nurse Turnover Prediction',
    category: 'Workforce',
    source: 'HR Analytics Team',
    notes: 'Data quality concerns',
    status: 'New',
  },
  {
    name: 'Supply Chain Cost Analytics',
    category: 'Supply Chain',
    source: 'Operations Review',
    notes: 'Linked to GPO renegotiation',
    status: 'New',
  },
];

interface DocumentListingEntry {
  id: string;
  category: string;
  name: string;
  description: string;
}

interface DocumentListing {
  id: string;
  fileName: string;
  entries: DocumentListingEntry[];
  use_case_count: number;
}

interface UseCaseDecisionRecord {
  use_case_id?: string | number;
  name?: string;
  category?: string;
  description?: string;
  decision_status?: string;
  file_name?: string;
  document_id?: string;
  candidate_id?: string | number | null;
  intake_outcome?: string | null;
  intake_reason?: string | null;
  total_count?: number;
}

interface DocumentWithCounts {
  document_id: string;
  file_name?: string;
  file_type?: string;
  uploaded_at?: string;
  document_status?: string;
  use_case_count: number;
}

interface UploadedDocument {
  id: string;
  name: string;
  uploadedAt: string;
  fileType: string;
  size: string;
  status: string;
}

interface InitiativesUploadBandProps {
  className?: string;
  defaultExpanded?: boolean;
}

export function InitiativesUploadBand({
  className = '',
  defaultExpanded = false,
}: InitiativesUploadBandProps) {
  const { session, sessionLoading } = useAuth();
  const clientId = session?.client_id || null;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [showFormatModal, setShowFormatModal] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState<UploadedDocument[]>([]);
  const [documentListings, setDocumentListings] = useState<DocumentListing[]>([]);
  const [expandedListingIds, setExpandedListingIds] = useState<string[]>([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const [isLoadingDocumentListings, setIsLoadingDocumentListings] = useState(false);
  const [approvedUseCaseIds, setApprovedUseCaseIds] = useState<string[]>([]);
  // New: Track use cases per document for lazy loading
  const [documentUseCases, setDocumentUseCases] = useState<Record<string, UseCaseDecisionRecord[]>>({});
  const [loadingDocumentIds, setLoadingDocumentIds] = useState<string[]>([]);
  const [useCasePageByDocument, setUseCasePageByDocument] = useState<Record<string, number>>({});
  const [approvingUseCaseIds, setApprovingUseCaseIds] = useState<string[]>([]);
  const [approveUseCaseError, setApproveUseCaseError] = useState<string | null>(null);
  const [promotedCandidateIds, setPromotedCandidateIds] = useState<string[]>([]);
  const [isIngesting, setIsIngesting] = useState(false);
  const [ingestionError, setIngestionError] = useState<string | null>(null);
  const [ingestionSuccess, setIngestionSuccess] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [newlyUploadedDocumentIds, setNewlyUploadedDocumentIds] = useState<string[]>([]);
  const socketRef = useRef<Socket | null>(null);
  const uploadedDocumentsRef = useRef<UploadedDocument[]>([]);
  const documentListingsRef = useRef<DocumentListing[]>([]);
  const expandedListingIdsRef = useRef<string[]>([]);
  const documentUseCasesRef = useRef<Record<string, UseCaseDecisionRecord[]>>({});
  const hasLoadedDocumentsRef = useRef(false);
  const hasLoadedListingsRef = useRef(false);
  const fetchUploadedDocumentsRef = useRef<(silent?: boolean) => Promise<void>>(async () => {});
  const fetchDocumentListingsRef = useRef<(silent?: boolean) => Promise<void>>(async () => {});

  uploadedDocumentsRef.current = uploadedDocuments;
  documentListingsRef.current = documentListings;
  expandedListingIdsRef.current = expandedListingIds;
  documentUseCasesRef.current = documentUseCases;

  const normalizeFileKey = (fileName: string) => fileName.trim().toLowerCase();

  const getStatusBadgeClasses = (status: string) => {
    switch (status.toLowerCase()) {
      case 'processed':
      case 'ready for review':
      case 'scanned':
        return 'bg-green-50 text-green-700';
      case 'processing':
        return 'bg-amber-50 text-amber-700';
      case 'failed':
      case 'quarantined':
        return 'bg-red-50 text-red-700';
      case 'uploaded':
        return 'bg-blue-50 text-blue-700';
      default:
        return 'bg-[#18365E]/5 text-[#18365E]';
    }
  };

  const matchedUploadedDocumentIds = new Set<string>();
  const unifiedDocuments = [
    ...documentListings.map(listing => {
      const matchedDocument = uploadedDocuments.find(document => (
        document.id === listing.id || normalizeFileKey(document.name) === normalizeFileKey(listing.fileName)
      ));

      if (matchedDocument) {
        matchedUploadedDocumentIds.add(matchedDocument.id);
      }

      return {
        id: matchedDocument?.id || listing.id,
        fileName: matchedDocument?.name || listing.fileName,
        fileType: matchedDocument?.fileType || 'CSV',
        size: matchedDocument?.size || 'Unknown size',
        uploadedAt: matchedDocument?.uploadedAt || 'Recently',
        status: matchedDocument?.status || 'Available',
        listingId: listing.id,
        useCaseCount: listing.use_case_count || 0,
      };
    }),
    ...uploadedDocuments
      .filter(document => !matchedUploadedDocumentIds.has(document.id))
      .map(document => ({
        id: document.id,
        fileName: document.name,
        fileType: document.fileType,
        size: document.size,
        uploadedAt: document.uploadedAt,
        status: document.status,
        listingId: null,
        useCaseCount: 0,
      })),
  ];

  const hasUploaded =
    !sessionLoading &&
    unifiedDocuments.length > 0;

  const formatFileSize = (sizeInBytes?: number) => {
    if (!sizeInBytes || Number.isNaN(sizeInBytes)) return 'Unknown size';
    if (sizeInBytes < 1024) return `${sizeInBytes} B`;
    if (sizeInBytes < 1024 * 1024) return `${(sizeInBytes / 1024).toFixed(1)} KB`;
    return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatUploadedDate = (uploadedAt?: string | { value?: string }) => {
    const rawValue = typeof uploadedAt === 'object' ? uploadedAt?.value : uploadedAt;

    if (!rawValue) return 'Recently';

    const parsedDate = new Date(rawValue);
    if (Number.isNaN(parsedDate.getTime())) return 'Recently';

    return parsedDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const normalizeStatus = (status?: string) => {
    switch ((status || '').toLowerCase()) {
      case 'processed':
        return 'Processed';
      case 'scanned':
        return 'Scanned';
      case 'processing':
        return 'Processing';
      case 'uploaded':
        return 'Uploaded';
      case 'failed':
        return 'Failed';
      case 'quarantined':
        return 'Quarantined';
      default:
        return status || 'Ready for Review';
    }
  };

  const applySocketStatusUpdate = (payload: unknown) => {
    if (!payload || typeof payload !== 'object') return;

    const event = payload as Record<string, unknown>;
    const eventClientId = String(event.client_id || '');
    const eventDocumentId = String(event.document_id || '');
    const eventStatus = typeof event.status === 'string' ? event.status : '';

    if (!eventClientId || !eventDocumentId || !eventStatus) return;
    if (String(clientId) !== eventClientId) return;

    setUploadedDocuments((prev) =>
      prev.map((doc) =>
        String(doc.id) === eventDocumentId
          ? { ...doc, status: normalizeStatus(eventStatus) }
          : doc
      )
    );
  };

  useEffect(() => {
    if (sessionLoading || !clientId) return;

    if (!SOCKET_BASE_URL) return;
    const socket = io(SOCKET_BASE_URL, {
      path: SOCKET_PATH,
      transports: ['polling', 'websocket'],
      withCredentials: true,
    });
    socketRef.current = socket;

    const handleStatusChanged = (payload: unknown) => {
      console.log('[InitiativesUploadBand socket] document:status-changed', payload);
      applySocketStatusUpdate(payload);
    };

    const handleDocumentStatusChanged = (payload: unknown) => {
      console.log('[InitiativesUploadBand socket] documentStatusChanged', payload);
      applySocketStatusUpdate(payload);
    };

    socket.on('document:status-changed', handleStatusChanged);
    socket.on('documentStatusChanged', handleDocumentStatusChanged);

    return () => {
      socket.off('document:status-changed', handleStatusChanged);
      socket.off('documentStatusChanged', handleDocumentStatusChanged);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [clientId, sessionLoading]);

  const mapApiDocument = (document: Record<string, any>): UploadedDocument => {
    const fileName =
      document.original_filename ||
      document.filename ||
      document.name ||
      'Uploaded initiative document';

    const detectedExtension = fileName.includes('.')
      ? fileName.split('.').pop()?.toUpperCase()
      : undefined;

    return {
      id: String(document.document_id || document.id || fileName),
      name: fileName,
      uploadedAt: formatUploadedDate(
        document.created_at ||
        document.uploaded_at ||
        document.updated_at,
      ),
      fileType: document.file_type?.toUpperCase() || detectedExtension || 'DOC',
      size: typeof document.file_size === 'number'
        ? formatFileSize(document.file_size)
        : typeof document.size === 'number'
          ? formatFileSize(document.size)
          : 'Unknown size',
      status: normalizeStatus(document.status),
    };
  };

  const isDocumentReadyForProceed = (status: string) => {
    const normalizedStatus = status.toLowerCase();
    return normalizedStatus === 'processed' || normalizedStatus === 'ready for review' || normalizedStatus === 'scanned';
  };

  const fetchUseCasesForDocument = async (
    documentId: string,
    options?: { force?: boolean; silent?: boolean },
  ) => {
    if (!clientId) return;

    const existing = documentUseCasesRef.current[documentId];
    if (!options?.force && existing) return;

    const showLoading = !options?.silent && !existing;

    try {
      if (showLoading) {
        setLoadingDocumentIds(prev => (prev.includes(documentId) ? prev : [...prev, documentId]));
      }

      const useCases: UseCaseDecisionRecord[] = [];
      for (let page = 1; ; page += 1) {
        const query = new URLSearchParams({
          client_id: String(clientId),
          page: String(page),
          pageSize: String(USE_CASES_FETCH_PAGE_SIZE),
        });
        const response = await fetch(
          `${API_BASE_URL}/api/v2/use-cases-user-decisions/by-document/${encodeURIComponent(documentId)}?${query.toString()}`,
          {
            credentials: 'include',
          },
        );

        if (!response.ok) {
          throw new Error(`Failed to load use cases (${response.status})`);
        }

        const payload = await response.json();
        const pageRows: UseCaseDecisionRecord[] = Array.isArray(payload?.use_cases) ? payload.use_cases : [];
        useCases.push(...pageRows);

        const totalCount = Number(pageRows[0]?.total_count) || 0;
        if (pageRows.length < USE_CASES_FETCH_PAGE_SIZE || useCases.length >= totalCount) break;
      }

      if (serializeUseCases(existing || []) !== serializeUseCases(useCases)) {
        setDocumentUseCases(prev => ({ ...prev, [documentId]: useCases }));
      }

      const serverApprovedIds = useCases
        .filter(uc => (uc.decision_status || '').toLowerCase() === 'approved')
        .map(uc => String(uc.use_case_id))
        .filter(id => id !== 'undefined' && id !== '');
      if (serverApprovedIds.length > 0) {
        setApprovedUseCaseIds(prev => Array.from(new Set([...prev, ...serverApprovedIds])));
      }
    } catch (error) {
      console.error('Error fetching use cases for document:', error);
      if (!existing) {
        setDocumentUseCases(prev => ({ ...prev, [documentId]: [] }));
      }
    } finally {
      if (showLoading) {
        setLoadingDocumentIds(prev => prev.filter(id => id !== documentId));
      }
    }
  };

  const fetchUploadedDocuments = async (silent = false) => {
    if (!clientId) return;

    const showLoading = !silent && !hasLoadedDocumentsRef.current;

    try {
      if (showLoading) setIsLoadingDocuments(true);
      if (!silent) setUploadError(null);

      const response = await fetch(
        `${API_BASE_URL}/api/v2/documents?clientId=${encodeURIComponent(clientId)}&document_kind=${encodeURIComponent(INITIATIVES_DOCUMENT_KIND)}`,
      );

      if (!response.ok) {
        throw new Error(`Failed to load documents (${response.status})`);
      }

      const payload = await response.json();
      const apiDocuments = payload.documents || payload.data || [];
      const nextDocuments = apiDocuments.map(mapApiDocument);

      if (serializeUploadedDocuments(uploadedDocumentsRef.current) !== serializeUploadedDocuments(nextDocuments)) {
        setUploadedDocuments(nextDocuments);
      }
    } catch (error) {
      console.error('Error fetching initiative documents:', error);
      if (!hasLoadedDocumentsRef.current) {
        setUploadedDocuments([]);
      }
    } finally {
      hasLoadedDocumentsRef.current = true;
      if (showLoading) setIsLoadingDocuments(false);
    }
  };

  const fetchDocumentListings = async (silent = false) => {
    if (!clientId) return;

    const showLoading = !silent && !hasLoadedListingsRef.current;

    try {
      if (showLoading) setIsLoadingDocumentListings(true);

      const query = new URLSearchParams({ client_id: String(clientId) });
      const response = await fetch(
        `${API_BASE_URL}/api/v2/use-cases-user-decisions/documents-with-counts?${query.toString()}`,
        {
          credentials: 'include',
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to load document listings (${response.status})`);
      }

      const payload = await response.json();
      const documents: DocumentWithCounts[] = Array.isArray(payload?.documents) ? payload.documents : [];

      const listings: DocumentListing[] = documents.map((doc, index) => ({
        id: String(doc.document_id || `doc-${index}`),
        fileName: doc.file_name || 'Unknown file',
        entries: [],
        use_case_count: doc.use_case_count || 0,
      }));

      const previousListings = documentListingsRef.current;
      const listingsChanged =
        serializeDocumentListings(previousListings) !== serializeDocumentListings(listings);

      if (listingsChanged) {
        setDocumentListings(listings);

        const previousCountById = new Map(
          previousListings.map((listing) => [listing.id, listing.use_case_count]),
        );
        const expandedIds = expandedListingIdsRef.current;

        if (listings.length > 0 && expandedIds.length === 0) {
          setExpandedListingIds([listings[0].id]);
          fetchUseCasesForDocument(listings[0].id);
        } else {
          listings.forEach((listing) => {
            if (
              expandedIds.includes(listing.id) &&
              previousCountById.get(listing.id) !== listing.use_case_count
            ) {
              fetchUseCasesForDocument(listing.id, { force: true, silent: true });
            }
          });
        }
      }
    } catch (error) {
      console.error('Error fetching document listings:', error);
      if (!hasLoadedListingsRef.current) {
        setDocumentListings([]);
        setExpandedListingIds([]);
      }
    } finally {
      hasLoadedListingsRef.current = true;
      if (showLoading) setIsLoadingDocumentListings(false);
    }
  };

  fetchUploadedDocumentsRef.current = fetchUploadedDocuments;
  fetchDocumentListingsRef.current = fetchDocumentListings;

  useEffect(() => {
    if (sessionLoading) return;

    if (!clientId) {
      setUploadedDocuments([]);
      setDocumentListings([]);
      setExpandedListingIds([]);
      hasLoadedDocumentsRef.current = false;
      hasLoadedListingsRef.current = false;
      return;
    }

    fetchUploadedDocuments();
    fetchDocumentListings();
  }, [clientId, sessionLoading]);

  useEffect(() => {
    if (sessionLoading || !clientId) return;

    const intervalId = window.setInterval(() => {
      void fetchUploadedDocumentsRef.current(true);
      void fetchDocumentListingsRef.current(true);
    }, LISTING_POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [clientId, sessionLoading]);

  const handleUploadClick = () => {
    if (!clientId) {
      alert('A client session is required before uploading documents.');
      return;
    }

    fileInputRef.current?.click();
  };

  const isUseCaseRecordApproved = (record: UseCaseDecisionRecord) => {
    const idStr = record.use_case_id !== undefined && record.use_case_id !== null
      ? String(record.use_case_id)
      : '';
    if (idStr && approvedUseCaseIds.includes(idStr)) return true;
    const outcome = (record.intake_outcome || '').toLowerCase();
    if (isSuccessfulFromDocumentOutcome(outcome)) return true;
    return (record.decision_status || '').toLowerCase() === 'approved';
  };

  const getCandidateId = (record: UseCaseDecisionRecord) =>
    record.candidate_id !== undefined && record.candidate_id !== null && String(record.candidate_id) !== ''
      ? String(record.candidate_id)
      : '';

  const pendingCandidateIds = Array.from(
    new Set(
      Object.values(documentUseCases)
        .flat()
        .filter(record => isUseCaseRecordApproved(record))
        .map(getCandidateId)
        .filter(id => id && !promotedCandidateIds.includes(id)),
    ),
  );

  const hasApprovedWithoutCandidate = Object.values(documentUseCases)
    .flat()
    .some(record => isUseCaseRecordApproved(record) && !getCandidateId(record));

  const ingestionHint = !hasUploaded
    ? 'Upload an initiatives file to get started.'
    : pendingCandidateIds.length === 0
      ? promotedCandidateIds.length > 0
        ? 'All approved use cases have been ingested. Approve more use cases to ingest them.'
        : hasApprovedWithoutCandidate
          ? 'Use cases approved in an earlier session can’t be ingested from here. Approve a use case in this session to enable ingestion.'
          : 'Approve at least one use case above to enable ingestion.'
      : `${pendingCandidateIds.length} approved use case${pendingCandidateIds.length === 1 ? '' : 's'} ready to ingest. Ingestion also runs measures, scoring, profile and ROI generation.`;

  const handleIngestionClick = async () => {
    if (!clientId || isIngesting || pendingCandidateIds.length === 0) return;

    const requestedIds = pendingCandidateIds;
    setIsIngesting(true);
    setIngestionError(null);
    setIngestionSuccess(null);

    try {
      const response = await batchPromoteCandidates({ candidateIds: requestedIds, clientId });
      const promotions: Array<{ candidate_id?: string | number }> = Array.isArray(response?.promotions)
        ? response.promotions
        : [];
      const promotedIds = promotions
        .map(p => String(p?.candidate_id ?? ''))
        .filter(id => requestedIds.includes(id));

      setPromotedCandidateIds(prev => Array.from(new Set([...prev, ...promotedIds])));

      const promotedCount = promotedIds.length;
      const failedCount = requestedIds.length - promotedCount;

      if (promotedCount === 0) {
        setIngestionError('No use cases were ingested. Please try again.');
      } else if (failedCount > 0) {
        setIngestionError(
          `${promotedCount} of ${requestedIds.length} use cases ingested. ${failedCount} could not be ingested — try again.`,
        );
      } else {
        setIngestionSuccess(
          `${promotedCount} use case${promotedCount === 1 ? '' : 's'} ingested. Measures, scoring, profiles and ROI are being generated.`,
        );
      }
    } catch (error) {
      console.error('Error ingesting use cases:', error);
      setIngestionError(error instanceof Error ? error.message : 'Ingestion failed. Please try again.');
    } finally {
      setIsIngesting(false);
    }
  };

  const handleProceedClick = (documentName: string) => {
    alert(`Proceeding with ${documentName}...`);
  };

  const toggleDocumentListing = (listingId: string) => {
    const isExpanding = !expandedListingIds.includes(listingId);

    setExpandedListingIds(prev => (
      prev.includes(listingId)
        ? prev.filter(id => id !== listingId)
        : [...prev, listingId]
    ));

    // Lazy load use cases when expanding
    if (isExpanding && !documentUseCases[listingId] && !loadingDocumentIds.includes(listingId)) {
      fetchUseCasesForDocument(listingId);
    }
  };

  const handleApproveUseCase = async (record: UseCaseDecisionRecord) => {
    const useCaseId = record.use_case_id;
    if (!clientId || useCaseId === undefined || useCaseId === null) {
      setApproveUseCaseError('Cannot approve: missing client or use case id.');
      return;
    }

    const idStr = String(useCaseId);
    if (approvingUseCaseIds.includes(idStr) || isUseCaseRecordApproved(record)) return;

    setApproveUseCaseError(null);
    setApprovingUseCaseIds(prev => [...prev, idStr]);

    try {
      const numericId = typeof useCaseId === 'number' ? useCaseId : Number(useCaseId);
      if (Number.isNaN(numericId)) {
        throw new Error('Invalid use case id.');
      }

      const response = await createUseCasesFromDocument({
        sourceUseCaseIds: [numericId],
        clientId,
        autoPromote: false,
      });

      const outcomeRow = findFromDocumentOutcome(response, numericId);
      const outcome = (outcomeRow?.outcome || '').toLowerCase();

      if (!outcome) {
        throw new Error('Approve succeeded but no outcome was returned for this use case.');
      }

      if (!isSuccessfulFromDocumentOutcome(outcome)) {
        const reason =
          outcomeRow?.reason ||
          `Use case was ${outcome}. It was not included in ingestion.`;
        setDocumentUseCases(prev => {
          const next = { ...prev };
          for (const docId of Object.keys(next)) {
            next[docId] = next[docId].map(uc =>
              String(uc.use_case_id) === idStr
                ? {
                    ...uc,
                    intake_outcome: outcome,
                    intake_reason: reason,
                    candidate_id: outcomeRow?.candidate_id ?? uc.candidate_id ?? null,
                  }
                : uc,
            );
          }
          return next;
        });
        setApproveUseCaseError(reason);
        return;
      }

      // staged | already_staged → success (idempotent)
      setApprovedUseCaseIds(prev => (prev.includes(idStr) ? prev : [...prev, idStr]));
      setDocumentUseCases(prev => {
        const next = { ...prev };
        for (const docId of Object.keys(next)) {
          next[docId] = next[docId].map(uc =>
            String(uc.use_case_id) === idStr
              ? {
                  ...uc,
                  decision_status: 'approved',
                  intake_outcome: outcome,
                  intake_reason: null,
                  // Keep candidate_id from response when present
                  candidate_id: outcomeRow?.candidate_id ?? uc.candidate_id ?? null,
                }
              : uc,
          );
        }
        return next;
      });
    } catch (error) {
      console.error('Error approving use case:', error);
      setApproveUseCaseError(error instanceof Error ? error.message : 'Approval failed. Please try again.');
    } finally {
      setApprovingUseCaseIds(prev => prev.filter(id => id !== idStr));
    }
  };

  const handleDownloadSampleTemplate = () => {
    const headers = ['Initiative Name', 'Category', 'Source', 'Notes', 'Status'];
    const rows = sampleInitiatives.map(initiative => [
      initiative.name,
      initiative.category,
      initiative.source,
      initiative.notes,
      initiative.status,
    ]);

    const csvLines = [headers, ...rows]
      .map(row => row.map(value => `"${String(value).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const csvBlob = new Blob([csvLines], { type: 'text/csv;charset=utf-8;' });
    const objectUrl = URL.createObjectURL(csvBlob);

    const link = document.createElement('a');
    link.href = objectUrl;
    link.setAttribute('download', 'initiatives-upload-sample.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  };

  const uploadFiles = async (selectedFiles: File[]) => {
    if (!selectedFiles.length || !clientId) {
      return;
    }

    const invalidFile = selectedFiles.find(file => {
      const extension = file.name.split('.').pop()?.toLowerCase() || '';
      return !ALLOWED_EXTENSIONS.includes(extension) || file.size > MAX_FILE_SIZE_BYTES;
    });

    if (invalidFile) {
      const extension = invalidFile.name.split('.').pop()?.toLowerCase() || '';
      if (!ALLOWED_EXTENSIONS.includes(extension)) {
        setUploadError(`Unsupported file type. Supported formats: ${ALLOWED_EXTENSIONS_LABEL}.`);
      } else {
        setUploadError('File size exceeds the 25MB limit.');
      }
      return;
    }

    try {
      setIsUploading(true);
      setUploadError(null);

      const uploadedIds: string[] = [];

      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('document_kind', INITIATIVES_DOCUMENT_KIND);

        const response = await fetch(
          `${API_BASE_URL}/api/v2/documents/upload?clientId=${encodeURIComponent(clientId)}`,
          {
            method: 'POST',
            body: formData,
          },
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || errorData.message || `Upload failed (${response.status})`);
        }

        const result = await response.json();
        const documentId = result.data?.document_id;

        if (documentId) {
          uploadedIds.push(String(documentId));
        }
      }

      if (uploadedIds.length > 0) {
        setNewlyUploadedDocumentIds(prev => Array.from(new Set([...prev, ...uploadedIds])));
      }

      await Promise.all([fetchUploadedDocuments(), fetchDocumentListings()]);
    } catch (error) {
      console.error('Error uploading initiative document:', error);
      setUploadError(error instanceof Error ? error.message : 'Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileSelection = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);

    if (!selectedFiles.length) {
      event.target.value = '';
      return;
    }

    if (!clientId) {
      alert('A client session is required before uploading documents.');
      event.target.value = '';
      return;
    }

    await uploadFiles(selectedFiles);
    event.target.value = '';
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!isUploading) {
      setIsDragActive(true);
    }
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const nextTarget = event.relatedTarget as Node | null;
    if (!event.currentTarget.contains(nextTarget)) {
      setIsDragActive(false);
    }
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragActive(false);

    if (isUploading) return;

    const droppedFiles = Array.from(event.dataTransfer.files || []);
    if (!droppedFiles.length) return;

    if (!clientId) {
      alert('A client session is required before uploading documents.');
      return;
    }

    await uploadFiles(droppedFiles);
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".txt,.csv,.pdf,.md"
        multiple
        className="hidden"
        onChange={handleFileSelection}
      />

      <div className={`rounded-xl overflow-hidden transition-all duration-200 ${isExpanded
        ? 'bg-white border border-[#e5e7eb] shadow-sm'
        : 'bg-white border border-[#e5e7eb] hover:border-[#18365E]/20'
        } ${className}`}>
        {/* Header - Always Visible */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`w-full flex items-center justify-between p-4 cursor-pointer transition-all duration-150 ease-out group ${isExpanded ? 'border-b border-[#e5e7eb]' : ''
            }`}
        >
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors duration-150 ${isExpanded
              ? 'bg-[#18365E] text-white'
              : 'bg-[#18365E]/10 text-[#18365E] group-hover:bg-[#18365E]/15'
              }`}>
              <Upload className="w-5 h-5" />
            </div>
            <div className="text-left">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-[#18365E]">
                  Existing Initiatives Upload
                </h3>
                {hasUploaded && (
                  <div className="flex items-center gap-1 text-[10px] font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                    <CheckCircle2 className="w-3 h-3" />
                    Uploaded
                  </div>
                )}
              </div>
              <p className="text-xs text-[#717182] mt-0.5">
                {isExpanded
                  ? 'Compare, deduplicate, and enrich your current initiatives'
                  : 'Upload your existing analytics initiatives for comparison'
                }
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <span className="hidden sm:block text-[10px] font-medium px-2 py-1 rounded bg-[#18365E]/5 text-[#717182]">
              Optional
            </span>
            {isExpanded ? (
              <ChevronUp className="w-5 h-5 text-[#18365E]" />
            ) : (
              <ChevronDown className="w-5 h-5 text-[#717182] group-hover:text-[#18365E] transition-colors" />
            )}
          </div>
        </button>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="p-4 bg-[#FAFBFC]">
            {/* Upload Dropzone */}
            <div
              className={`mb-4 rounded-xl border-2 border-dashed p-4 bg-white transition-colors ${isDragActive
                ? 'border-[#18365E] bg-[#18365E]/[0.03]'
                : 'border-[#e5e7eb] hover:border-[#18365E]/30'
                } ${isUploading ? 'pointer-events-none opacity-80' : ''}`}
              onDragOver={handleDragOver}
              onDragEnter={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="flex flex-col items-center justify-center py-4">
                <div className="w-12 h-12 rounded-full bg-[#18365E]/5 flex items-center justify-center mb-3">
                  <Upload className="w-6 h-6 text-[#18365E]" />
                </div>
                <p className="text-sm font-medium text-[#18365E] mb-1">
                  {isDragActive ? 'Drop your file to upload' : 'Drag and drop your file here'}
                </p>
                <p className="text-xs text-[#717182] mb-3">
                  Supported formats: {ALLOWED_EXTENSIONS_LABEL} (Max 25MB)
                </p>
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <button
                    onClick={handleUploadClick}
                    disabled={isUploading || sessionLoading}
                    className="px-4 py-2 text-xs font-medium text-[#18365E] bg-white border border-[#e5e7eb] rounded-lg hover:bg-[#F5F7FA] transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isUploading ? 'Uploading...' : 'Browse files'}
                  </button>

                  <button
                    type="button"
                    onClick={handleDownloadSampleTemplate}
                    className="px-4 py-2 text-xs font-medium text-[#18365E] bg-white border border-[#e5e7eb] rounded-lg hover:bg-[#F5F7FA] transition-colors"
                  >
                    Download sample CSV
                  </button>
                </div>
              </div>

              {uploadError && (
                <p className="mt-3 text-center text-xs font-medium text-[#b42318]">
                  {uploadError}
                </p>
              )}
            </div>

            {/* Documents Listing Section */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <FileSpreadsheet className="w-4 h-4 text-[#18365E]" />
                  <span className="text-xs font-semibold text-[#18365E]">
                    Documents Listing
                  </span>
                </div>
                <span className="text-xs text-[#717182]">
                  {unifiedDocuments.length} files available
                </span>
              </div>

              <p className="mb-3 text-xs text-[#717182]">
                Reference files already available for initiative comparison and enrichment.
              </p>

              <div className="space-y-3">
                {(isLoadingDocuments || isLoadingDocumentListings) && unifiedDocuments.length === 0 ? (
                  <div className="rounded-lg border border-[#e5e7eb] bg-white px-3 py-4 text-center text-xs font-medium text-[#717182]">
                    Loading documents...
                  </div>
                ) : unifiedDocuments.length === 0 ? (
                  <div className="rounded-lg border border-[#e5e7eb] bg-white px-3 py-4 text-center text-xs font-medium text-[#717182]">
                    No initiative documents uploaded yet.
                  </div>
                ) : unifiedDocuments.map(document => {
                  const listingId = document.listingId;
                  const isExpandable = Boolean(listingId);
                  const isOpen = listingId ? expandedListingIds.includes(listingId) : false;
                  const shouldShowProceed = newlyUploadedDocumentIds.includes(document.id) && isDocumentReadyForProceed(document.status);
                  const listingUseCases = (listingId && documentUseCases[listingId]) || [];
                  const useCasePageCount = Math.max(1, Math.ceil(listingUseCases.length / USE_CASES_TABLE_PAGE_SIZE));
                  const useCasePage = Math.min(
                    (listingId && useCasePageByDocument[listingId]) || 1,
                    useCasePageCount,
                  );
                  const useCasePageStart = (useCasePage - 1) * USE_CASES_TABLE_PAGE_SIZE;
                  const visibleUseCases = listingUseCases.slice(
                    useCasePageStart,
                    useCasePageStart + USE_CASES_TABLE_PAGE_SIZE,
                  );
                  const setUseCasePage = (page: number) => {
                    if (listingId) setUseCasePageByDocument(prev => ({ ...prev, [listingId]: page }));
                  };

                  return (
                    <div
                      key={document.id}
                      className="overflow-hidden rounded-lg border border-[#e5e7eb] bg-white"
                    >
                      <div className={`flex flex-col gap-3 px-3 py-3 sm:flex-row sm:items-center sm:justify-between ${isExpandable ? 'hover:bg-[#FAFBFC]' : ''}`}>
                        <button
                          type="button"
                          onClick={() => listingId && toggleDocumentListing(listingId)}
                          disabled={!isExpandable}
                          className="flex min-w-0 flex-1 items-start gap-2 text-left disabled:cursor-default"
                        >
                          {isExpandable ? (
                            isOpen ? (
                              <ChevronDown className="mt-0.5 w-4 h-4 shrink-0 text-[#18365E]" />
                            ) : (
                              <ChevronRight className="mt-0.5 w-4 h-4 shrink-0 text-[#18365E]" />
                            )
                          ) : (
                            <FileText className="mt-0.5 w-4 h-4 shrink-0 text-[#18365E]" />
                          )}

                          <div className="min-w-0">
                            <span className="block truncate text-sm font-medium text-[#18365E]">
                              {document.fileName}
                            </span>
                            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#717182]">
                              <span>{document.fileType}</span>
                              <span>{document.size}</span>
                              <span>Uploaded {document.uploadedAt}</span>
                            </div>
                          </div>
                        </button>

                        <div className="flex shrink-0 flex-wrap items-center gap-2">
                          <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${getStatusBadgeClasses(document.status)}`}>
                            {document.status}
                          </span>
                          <span className="rounded-full bg-[#18365E]/5 px-2 py-0.5 text-[10px] font-semibold text-[#18365E]">
                            {document.useCaseCount} use cases
                          </span>
                          {shouldShowProceed && (
                            <button
                              type="button"
                              onClick={() => handleProceedClick(document.fileName)}
                              className="rounded-lg bg-[#18365E] px-3 py-1.5 text-[11px] font-semibold text-white transition-colors hover:bg-[#0f2646]"
                            >
                              Proceed
                            </button>
                          )}
                        </div>
                      </div>

                      {listingId && isOpen && (
                        <div className="border-t border-[#e5e7eb] bg-[#FAFBFC] p-3">
                          {approveUseCaseError && (
                            <p className="mb-2 rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-[11px] font-medium text-[#b42318]">
                              {approveUseCaseError}
                            </p>
                          )}
                          {!documentUseCases[listingId] ? (
                            <div className="text-center py-4 text-xs text-[#717182]">
                              Loading use cases...
                            </div>
                          ) : documentUseCases[listingId].length === 0 ? (
                            <div className="text-center py-4 text-xs text-[#717182]">
                              No use cases found for this document.
                            </div>
                          ) : (
                            <div className="overflow-x-auto rounded-lg border border-[#e5e7eb] bg-white">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="bg-[#F5F7FA]">
                                    <th className="text-left px-3 py-2 font-semibold text-[#18365E] border-b border-[#e5e7eb]">
                                      Category
                                    </th>
                                    <th className="text-left px-3 py-2 font-semibold text-[#18365E] border-b border-[#e5e7eb]">
                                      Name
                                    </th>
                                    <th className="text-left px-3 py-2 font-semibold text-[#18365E] border-b border-[#e5e7eb] min-w-[260px]">
                                      Description
                                    </th>
                                    <th className="text-left px-3 py-2 font-semibold text-[#18365E] border-b border-[#e5e7eb]">
                                      Action
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {visibleUseCases.map((record, recordIndex) => {
                                    const useCaseIdStr =
                                      record.use_case_id !== undefined && record.use_case_id !== null
                                        ? String(record.use_case_id)
                                        : '';
                                    const isApproved = isUseCaseRecordApproved(record);
                                    const isApproving = useCaseIdStr && approvingUseCaseIds.includes(useCaseIdStr);

                                    return (
                                    <tr
                                      key={`${listingId}-${record.use_case_id ?? recordIndex}`}
                                      className={recordIndex % 2 === 0 ? 'bg-white' : 'bg-[#FAFBFC]'}
                                    >
                                      <td className="px-3 py-2 text-[#18365E] font-medium align-top">
                                        {record.category || 'Uncategorized'}
                                      </td>
                                      <td className="px-3 py-2 text-[#18365E] align-top">
                                        {record.name || 'Untitled Use Case'}
                                      </td>
                                      <td className="px-3 py-2 text-[#717182] align-top">
                                        {record.description || 'No description provided.'}
                                      </td>
                                      <td className="px-3 py-2 align-top">
                                        {isApproved ? (
                                          <div className="flex flex-col gap-1">
                                            {promotedCandidateIds.includes(getCandidateId(record)) ? (
                                              <span className="inline-flex w-fit rounded-full bg-[#18365E]/10 px-2 py-0.5 text-[10px] font-semibold text-[#18365E]">
                                                Ingested
                                              </span>
                                            ) : (
                                              <span className="inline-flex w-fit rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-semibold text-green-700">
                                                Approved
                                              </span>
                                            )}
                                            {record.candidate_id != null && String(record.candidate_id) !== '' && (
                                              <span className="text-[10px] text-[#717182]">
                                                candidate: {String(record.candidate_id)}
                                              </span>
                                            )}
                                          </div>
                                        ) : record.intake_outcome === 'rejected' || record.intake_outcome === 'skipped' ? (
                                          <div className="flex flex-col gap-1 max-w-[220px]">
                                            <span className="inline-flex w-fit rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800 capitalize">
                                              {record.intake_outcome}
                                            </span>
                                            {record.intake_reason && (
                                              <span className="text-[10px] leading-snug text-[#b45309]">
                                                {record.intake_reason}
                                              </span>
                                            )}
                                            {useCaseIdStr && (
                                              <button
                                                type="button"
                                                disabled={Boolean(isApproving)}
                                                onClick={() => handleApproveUseCase(record)}
                                                className="mt-0.5 w-fit rounded-md border border-[#18365E] px-2 py-0.5 text-[10px] font-semibold text-[#18365E] hover:bg-[#F3F4F6] disabled:cursor-not-allowed disabled:opacity-60"
                                              >
                                                {isApproving ? 'Retrying…' : 'Retry'}
                                              </button>
                                            )}
                                          </div>
                                        ) : !useCaseIdStr ? (
                                          <span className="text-[10px] font-medium text-[#717182]">—</span>
                                        ) : (
                                          <button
                                            type="button"
                                            disabled={Boolean(isApproving)}
                                            onClick={() => handleApproveUseCase(record)}
                                            className="rounded-md bg-[#18365E] px-2.5 py-1 text-[10px] font-semibold text-white transition-colors hover:bg-[#0f2646] disabled:cursor-not-allowed disabled:opacity-60"
                                          >
                                            {isApproving ? 'Approving…' : 'Approve'}
                                          </button>
                                        )}
                                      </td>
                                    </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                              {listingUseCases.length > USE_CASES_TABLE_PAGE_SIZE && (
                                <div className="flex items-center justify-between border-t border-[#e5e7eb] px-3 py-2 text-[11px] text-[#717182]">
                                  <span>
                                    Showing {useCasePageStart + 1}–{useCasePageStart + visibleUseCases.length} of {listingUseCases.length} use cases
                                  </span>
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={() => setUseCasePage(useCasePage - 1)}
                                      disabled={useCasePage <= 1}
                                      className="rounded-md border border-[#e5e7eb] px-2 py-0.5 font-semibold text-[#18365E] hover:bg-[#F5F7FA] disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                      Previous
                                    </button>
                                    <span>
                                      Page {useCasePage} of {useCasePageCount}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => setUseCasePage(useCasePage + 1)}
                                      disabled={useCasePage >= useCasePageCount}
                                      className="rounded-md border border-[#e5e7eb] px-2 py-0.5 font-semibold text-[#18365E] hover:bg-[#F5F7FA] disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                      Next
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* CTA Row */}
            <div className="flex items-center justify-between pt-2">
              <button
                onClick={() => setShowFormatModal(true)}
                className="text-xs font-medium text-[#18365E] hover:text-[#FF6E4C] transition-colors flex items-center gap-1"
              >
                <Info className="w-3 h-3" />
                View sample format
              </button>

              <button
                type="button"
                onClick={handleIngestionClick}
                disabled={isIngesting || pendingCandidateIds.length === 0}
                title={ingestionHint}
                className="px-4 py-2 text-xs font-semibold text-white bg-[#18365E] rounded-lg transition-colors hover:bg-[#0f2646] disabled:bg-[#18365E]/50 disabled:cursor-not-allowed"
              >
                {isIngesting
                  ? 'Ingesting…'
                  : pendingCandidateIds.length > 0
                    ? `Start Ingestion (${pendingCandidateIds.length})`
                    : 'Start Ingestion'}
              </button>
            </div>
            <p className="text-right text-[11px] text-[#717182]">{ingestionHint}</p>
            {ingestionError && (
              <p className="rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-[11px] font-medium text-[#b42318]">
                {ingestionError}
              </p>
            )}
            {ingestionSuccess && (
              <p className="rounded-md border border-green-200 bg-green-50 px-2 py-1.5 text-[11px] font-medium text-green-700">
                {ingestionSuccess}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Format Modal */}
      {showFormatModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowFormatModal(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-5"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-sm font-bold text-[#18365E] mb-3">
              Expected CSV/XLSX Format
            </h3>
            <p className="text-xs text-[#717182] mb-3">
              Your file should include the following columns:
            </p>
            <div className="bg-[#F5F7FA] rounded-lg p-3 mb-4">
              <ul className="space-y-1 text-xs text-[#18365E]">
                <li><span className="font-semibold">Initiative Name</span> — Name of the initiative (required)</li>
                <li><span className="font-semibold">Category</span> — Domain or category</li>
                <li><span className="font-semibold">Source</span> — Where this came from</li>
                <li><span className="font-semibold">Notes</span> — Additional context</li>
                <li><span className="font-semibold">Status</span> — Current status (optional)</li>
              </ul>
            </div>
            <button
              onClick={() => setShowFormatModal(false)}
              className="w-full px-4 py-2 text-xs font-semibold text-white bg-[#18365E] rounded-lg hover:bg-[#18365E]/90 transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default InitiativesUploadBand;